import { Shop } from "@cabukaka/types";
import { Store, MapPin, Star } from "lucide-react";
import { NavLink } from "../../ui/NavLink";

interface ShopCardProps {
    shop: Shop;
    showRating?: boolean;
    showLocation?: boolean;
}

export function ShopCard({ shop, showRating = true, showLocation = true }: ShopCardProps) {
    return (
        <NavLink to={`/boutiques/${shop.id}`} className="block group">
            <div className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                {/* Couverture avec effet de profondeur */}
                <div className="relative h-36 bg-gray-100 overflow-hidden">
                    {shop.coverImage ? (
                        <img 
                            src={shop.coverImage} 
                            alt={shop.name} 
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
                    )}
                    
                    {/* Logo flottant avec ombre portée */}
                    <div className="absolute -bottom-6 left-5">
                        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white shadow-lg border-4 border-white">
                            {shop.logo ? (
                                <img 
                                    src={shop.logo} 
                                    alt={shop.name} 
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                                    <Store className="w-8 h-8 text-primary/40" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Infos */}
                <div className="p-5 pt-9">
                    <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors line-clamp-1 text-lg">
                        {shop.name}
                    </h3>
                    
                    <p className="text-sm text-gray-500 mt-1.5 line-clamp-2 min-h-[2.5em]">
                        {shop.description || "Découvrez nos produits frais et nos services de qualité."}
                    </p>

                    <div className="flex items-center gap-4 mt-4 text-xs">
                        {showRating && shop.rating > 0 && (
                            <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                <span>{shop.rating}</span>
                                <span className="text-amber-600/60 font-normal">({shop.reviews})</span>
                            </div>
                        )}
                        
                        {showLocation && shop.location?.city && (
                            <div className="flex items-center gap-1 text-gray-500 font-medium">
                                <MapPin className="w-3.5 h-3.5 text-primary" />
                                <span>{shop.location.city}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </NavLink>
    );
}